/**
 * The KYB vocabulary, in one place.
 *
 * The server enforces the lifecycle (`Verification cannot move from X to Y`)
 * and requires a reason on the two customer-facing decisions. Rather than let
 * reviewers discover those rules through error toasts, every button set on the
 * detail screen is derived from the tables below.
 */

/** Queue tabs. `ALL` is a client-side convenience — the API filter omits it. */
export const KYB_STATUS_TABS = [
  {key: 'ALL', label: 'All'},
  {key: 'SUBMITTED', label: 'Submitted'},
  {key: 'UNDER_REVIEW', label: 'Under review'},
  {key: 'MORE_INFO', label: 'More info needed'},
  {key: 'APPROVED', label: 'Approved'},
  {key: 'REJECTED', label: 'Rejected'},
  {key: 'DRAFT', label: 'Draft'},
];

/** The statuses we count for the queue badges — `ALL` is not a real status. */
export const KYB_COUNTABLE_STATUSES = KYB_STATUS_TABS.filter(
  tab => tab.key !== 'ALL',
).map(tab => tab.key);

/**
 * The server's transition map, mirrored.
 *
 * `DRAFT → SUBMITTED` and `REJECTED|MORE_INFO → SUBMITTED` exist server-side
 * but are customer-side moves, so no admin action leads out of those states.
 * `APPROVED` is terminal: there is no un-approve operation.
 */
export const ALLOWED_TRANSITIONS = {
  DRAFT: [],
  SUBMITTED: ['UNDER_REVIEW'],
  UNDER_REVIEW: ['APPROVED', 'REJECTED', 'MORE_INFO'],
  APPROVED: [],
  REJECTED: [],
  MORE_INFO: [],
};

/**
 * Statuses an admin may submit on the business's behalf.
 *
 * `submitKybForReview` is the customer's own move out of these states; admins
 * may call it too, for a business that sent its files by email. The server
 * re-checks the checklist and answers with what is missing instead of moving.
 */
export const KYB_SUBMITTABLE_STATUSES = ['DRAFT', 'MORE_INFO', 'REJECTED'];

/** Statuses whose `setKybStatus` call is rejected without a `reason`. */
export const REASON_REQUIRED_STATUSES = ['REJECTED', 'MORE_INFO'];

/**
 * When document review controls are allowed to show.
 *
 * The old `reviewBusinessDocument` never checked the account status, and the
 * Hub's `reviewKycHubDocument` is not documented to, so a file could be
 * accepted while the account is still `DRAFT` — before anyone submitted
 * anything. The guard is ours to apply.
 */
export const DOC_REVIEWABLE_ACCOUNT_STATUSES = ['SUBMITTED', 'UNDER_REVIEW'];

/**
 * When a file may be replaced or archived. The server refuses anything else
 * with an error; hiding the buttons saves the reviewer the round trip.
 */
export const DOC_MODIFIABLE_ACCOUNT_STATUSES = [
  'DRAFT',
  'REJECTED',
  'MORE_INFO',
];

/** Copy for each decision, keyed by the status it moves the account to. */
export const KYB_ACTION_COPY = {
  UNDER_REVIEW: {
    label: 'Start review',
    title: 'Start review',
    description:
      'Marks the account as being reviewed and emails the business that their submission is being looked at. It also acts as the informal claim, so another reviewer can see it is taken.',
    confirm: 'Start review',
  },
  APPROVED: {
    label: 'Approve',
    title: 'Approve verification',
    description:
      'Approves KYB for this business. This cannot be undone — there is no un-approve operation.',
    confirm: 'Approve',
  },
  MORE_INFO: {
    label: 'Request more info',
    title: 'Request more information',
    description:
      'Sends the business back to fix their submission. Reject the specific files first, so their checklist asks for exactly the right re-uploads.',
    confirm: 'Request more info',
  },
  REJECTED: {
    label: 'Reject',
    title: 'Reject verification',
    description:
      'Declines the submission. The business can correct it and submit again.',
    confirm: 'Reject',
  },
};

/**
 * How the contact's authority over the business reads on screen.
 *
 * The raw enum humanises badly — `OWNER_GT_25` becomes "Owner gt 25" — and
 * this string is the reason a power-of-attorney account carries an extra
 * checklist row, so it is worth spelling out. Unknown values fall back to the
 * generic humaniser rather than rendering blank.
 */
export const CONTACT_AUTHORITY_LABELS = {
  OWNER_GT_25: 'Owner with a 25% or greater stake',
  POWER_OF_ATTORNEY: 'Power of attorney',
  DIRECTOR: 'Director',
  LEGAL_REPRESENTATIVE: 'Legal representative',
};

export const DOCUMENT_STATUS_COLORS = {
  ACCEPTED: 'green',
  REJECTED: 'red',
  PENDING: 'orange',
};

export const KYB_STATUS_COLORS = {
  APPROVED: 'green',
  REJECTED: 'red',
  MORE_INFO: 'orange',
  UNDER_REVIEW: 'blue',
  SUBMITTED: 'purple',
  DRAFT: 'default',
};

export const ACCOUNT_STATUS_COLORS = {
  ACTIVE: 'green',
  PENDING_VERIFICATION: 'orange',
  RESTRICTED: 'red',
};

/** How long a presigned document link stays valid, in seconds. */
export const PRESIGN_EXPIRY_SECONDS = 900;
